import React, { useState, useMemo, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useOwnerAdmin } from '../../context/OwnerAdminContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import LastDeployedBadge from './LastDeployedBadge';
import DeployAlertBanner from './DeployAlertBanner';
import {
  LayoutDashboard,
  Users,
  Building2,
  CreditCard,
  MessageSquare,
  Activity,
  Flag,
  FileText,
  UserCog,
  Settings,
  LogOut,
  ChevronRight,
  Shield,
  Menu,
  X,
  Search,
  ChevronDown,
  Lock,
  Eye,
  EyeOff,
  Crown,
  ShieldCheck,
  Headphones,
  Code,
  Brain,
  TrendingUp,
  Sparkles,
  AlertOctagon,
  Mail,
  Layers,
  Globe,
  PhoneCall,
  PlugZap,
} from 'lucide-react';

// Navigation items grouped by category
const navigationGroups = [
  {
    name: 'Overview',
    items: [
      { name: 'Dashboard', href: '/owner-admin', icon: LayoutDashboard, permission: 'view_dashboard', exact: true },
    ]
  },
  {
    name: 'Customers',
    items: [
      { name: 'Customers', href: '/owner-admin/customers', icon: Building2, permission: 'view_customers' },
      { name: 'Users', href: '/owner-admin/users', icon: Users, permission: 'view_users' },
      { name: 'Seats & Capacity', href: '/owner-admin/seats', icon: Layers, permission: 'view_customers' },
      { name: 'Unpaid Sweep', href: '/owner-admin/unpaid-sweep', icon: AlertOctagon, permission: 'view_customers' },
    ]
  },
  {
    name: 'Operations',
    items: [
      { name: 'Health & To-Do', href: '/owner-admin/operations', icon: Activity, permission: 'view_dashboard' },
      { name: 'Billing', href: '/owner-admin/billing', icon: CreditCard, permission: 'view_billing' },
      { name: 'Payments Ledger', href: '/owner-admin/payments', icon: CreditCard, permission: 'view_dashboard' },
      { name: 'Appointments', href: '/owner-admin/appointments', icon: PhoneCall, permission: 'view_dashboard' },
      { name: 'AI Usage', href: '/owner-admin/ai-usage', icon: Brain, permission: 'view_billing' },
      { name: 'Funnel Analytics', href: '/owner-admin/funnel-analytics', icon: TrendingUp, permission: 'view_billing' },
      { name: 'Website Traffic', href: '/owner-admin/traffic', icon: Globe, permission: 'view_dashboard' },
      { name: 'Cost Dashboard', href: '/owner-admin/cost-dashboard', icon: Activity, permission: 'view_billing' },
      { name: 'Telemetry', href: '/owner-admin/telemetry', icon: Sparkles, permission: 'view_dashboard' },
      { name: 'Email Rules', href: '/owner-admin/email-rules', icon: Mail, permission: 'manage_config' },
      { name: 'GoHighLevel', href: '/owner-admin/ghl', icon: PlugZap, permission: 'view_dashboard' },
      { name: 'Homepage CMS', href: '/owner-admin/homepage', icon: FileText, permission: 'edit_settings', fallbackPermission: 'view_dashboard' },
      { name: 'Support', href: '/owner-admin/support', icon: MessageSquare, permission: 'view_tickets' },
    ]
  },
  {
    name: 'System',
    items: [
      { name: 'System Health', href: '/owner-admin/system', icon: Activity, permission: 'view_system_health' },
      { name: 'Feature Flags', href: '/owner-admin/features', icon: Flag, permission: 'manage_feature_flags' },
      { name: 'Audit Log', href: '/owner-admin/audit', icon: FileText, permission: 'view_audit_log' },
      { name: 'Client Errors', href: '/owner-admin/client-errors', icon: AlertOctagon, permission: 'view_system_health' },
    ]
  },
  {
    name: 'Administration',
    items: [
      { name: 'Staff', href: '/owner-admin/staff', icon: UserCog, permission: 'manage_staff' },
      { name: 'Settings', href: '/owner-admin/settings', icon: Settings, permission: 'edit_settings', fallbackPermission: 'view_dashboard' },
    ]
  },
];

// Role display names, colors, and icons
const ROLE_DISPLAY = {
  owner: { label: 'Owner', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Crown },
  super_admin: { label: 'Super Admin', color: 'bg-violet-500/20 text-violet-400 border-violet-500/30', icon: ShieldCheck },
  support_manager: { label: 'Support Manager', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Headphones },
  support_agent: { label: 'Support Agent', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: Users },
  billing_specialist: { label: 'Billing', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CreditCard },
  developer: { label: 'Developer', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Code },
  auditor: { label: 'Auditor', color: 'bg-muted0/20 text-muted-foreground border-white/10', icon: Eye },
};

export default function OwnerAdminLayout() {
  const { 
    admin, 
    logout, 
    hasPermission, 
    isAuthenticated, 
    loading,
    // View As feature
    viewAsRole,
    canViewAs,
    startViewAs,
    exitViewAs,
    effectiveRole,
    allRoles
  } = useOwnerAdmin();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Filter navigation based on permissions
  const filteredNavGroups = useMemo(() => {
    return navigationGroups.map(group => ({
      ...group,
      items: group.items.filter(item => {
        // Check primary permission or fallback permission
        return hasPermission(item.permission) || 
          (item.fallbackPermission && hasPermission(item.fallbackPermission));
      })
    })).filter(group => group.items.length > 0);
  }, [hasPermission]);

  // Count total accessible items
  const accessibleCount = useMemo(() => {
    return filteredNavGroups.reduce((acc, group) => acc + group.items.length, 0);
  }, [filteredNavGroups]);

  // Count total items
  const totalCount = useMemo(() => {
    return navigationGroups.reduce((acc, group) => acc + group.items.length, 0);
  }, []);

  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Pin dark theme on <body> so Radix portals (dialogs, dropdowns, selects)
  // rendered outside .owner-admin-scope still get the admin dark variables.
  useEffect(() => {
    document.body.classList.add('owner-admin-dark-portals');
    return () => document.body.classList.remove('owner-admin-dark-portals');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-card flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/owner-admin/login" replace />;
  }

  // Use effective role (viewAs or actual)
  const roleDisplay = ROLE_DISPLAY[effectiveRole] || ROLE_DISPLAY.support_agent;
  const actualRoleDisplay = ROLE_DISPLAY[admin?.role] || ROLE_DISPLAY.support_agent;

  return (
    <div className="owner-admin-scope min-h-screen bg-card">
      {/* View As Banner */}
      {viewAsRole && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-2">
          <div className="max-w-screen-xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-white" />
              <span className="text-white font-medium">
                Viewing as <span className="font-bold">{ROLE_DISPLAY[viewAsRole]?.label || viewAsRole}</span>
              </span>
              <span className="text-white/70 text-sm">
                — This is a preview of what a {ROLE_DISPLAY[viewAsRole]?.label} user would see
              </span>
            </div>
            <Button 
              size="sm" 
              variant="secondary"
              onClick={exitViewAs}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <EyeOff className="h-4 w-4 mr-2" />
              Exit View As
            </Button>
          </div>
        </div>
      )}

      {/* Mobile sidebar toggle */}
      <div className={cn(
        "lg:hidden fixed left-0 right-0 z-40 bg-card border-b border-white/15 px-4 py-3 flex items-center justify-between",
        viewAsRole ? "top-10" : "top-0"
      )}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <span className="font-semibold text-white">Owner Admin</span>
        </div>
        <Badge variant="outline" className={roleDisplay.color}>
          {roleDisplay.label}
        </Badge>
      </div>

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-30 w-64 bg-card border-r border-white/15 transform transition-transform duration-200 lg:translate-x-0 flex flex-col",
        sidebarOpen ? "translate-x-0" : "-translate-x-full",
        viewAsRole ? "pt-10" : ""
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/15 shrink-0">
          <img src="/countbucks-icon-light.png" alt="CountBucks" className="w-9 h-9 mr-3 block dark:hidden" />
          <img src="/countbucks-icon.png" alt="CountBucks" className="w-9 h-9 mr-3 hidden dark:block" />
          <div>
            <span className="font-bold text-lg bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent">CountBucks</span>
            <span className="block text-xs text-muted-foreground">Owner Admin</span>
          </div>
        </div>

        {/* Role indicator */}
        <div className="px-4 py-3 border-b border-white/10 shrink-0">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className={cn("text-xs", roleDisplay.color)}>
              {roleDisplay.label}
            </Badge>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground">
                    {accessibleCount}/{totalCount} pages
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-xs">You have access to {accessibleCount} of {totalCount} admin pages based on your role</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Role preview selector - Only for Owner/Super Admin */}
        {canViewAs && (
          <div className="px-4 py-3 border-b border-white/10 shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1 cursor-help">
                    <Eye className="h-3 w-3" />
                    Preview role view
                  </p>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[240px]">
                  <p className="text-xs">
                    Simulates what an employee with this admin role (e.g. Support Manager)
                    would see in this panel. It doesn&apos;t change your own access — pick
                    &quot;Owner&quot; or hit Exit to go back.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Select 
              value={viewAsRole || admin?.role} 
              onValueChange={(value) => startViewAs(value)}
            >
              <SelectTrigger className="w-full h-9 bg-white/10 border-white/20 text-white text-sm">
                <SelectValue placeholder="Select role to preview" />
              </SelectTrigger>
              <SelectContent>
                {allRoles.map((role) => {
                  const display = ROLE_DISPLAY[role];
                  const RoleIcon = display?.icon || Users;
                  const isCurrentRole = role === admin?.role;
                  return (
                    <SelectItem key={role} value={role}>
                      <div className="flex items-center gap-2">
                        <RoleIcon className="h-4 w-4" />
                        <span>{display?.label || role}</span>
                        {isCurrentRole && (
                          <span className="text-xs text-muted-foreground">(You)</span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {viewAsRole && (
              <Button
                variant="ghost"
                size="sm"
                onClick={exitViewAs}
                className="w-full mt-2 h-7 text-xs text-muted-foreground hover:text-white"
              >
                <EyeOff className="h-3 w-3 mr-1" />
                Exit preview mode
              </Button>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-4">
          {filteredNavGroups.map((group) => (
            <div key={group.name}>
              <button
                onClick={() => toggleGroup(group.name)}
                className="flex items-center justify-between w-full px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-muted-foreground"
              >
                {group.name}
                <ChevronDown className={cn(
                  "h-3 w-3 transition-transform",
                  collapsedGroups[group.name] ? "-rotate-90" : ""
                )} />
              </button>
              {!collapsedGroups[group.name] && (
                <div className="mt-1 space-y-1">
                  {group.items.map((item) => {
                    const isActive = item.exact 
                      ? location.pathname === item.href
                      : location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`owner-admin-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                          isActive
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "text-muted-foreground hover:text-white hover:bg-white/10"
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Show locked sections for awareness */}
          {accessibleCount < totalCount && (
            <div className="pt-4 border-t border-white/10">
              <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Restricted
              </p>
              <div className="mt-1 space-y-1">
                {navigationGroups.flatMap(group => 
                  group.items.filter(item => 
                    !hasPermission(item.permission) && 
                    !(item.fallbackPermission && hasPermission(item.fallbackPermission))
                  )
                ).slice(0, 3).map((item) => (
                  <TooltipProvider key={item.name}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground cursor-not-allowed">
                          <item.icon className="h-5 w-5" />
                          {item.name}
                          <Lock className="h-3 w-3 ml-auto" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="text-xs">Requires {item.permission.replace(/_/g, ' ')} permission</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
                {navigationGroups.flatMap(g => g.items).filter(item => 
                  !hasPermission(item.permission) && 
                  !(item.fallbackPermission && hasPermission(item.fallbackPermission))
                ).length > 3 && (
                  <p className="px-3 py-1 text-xs text-muted-foreground">
                    +{navigationGroups.flatMap(g => g.items).filter(item => 
                      !hasPermission(item.permission) && 
                      !(item.fallbackPermission && hasPermission(item.fallbackPermission))
                    ).length - 3} more restricted
                  </p>
                )}
              </div>
            </div>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-white/15 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 text-left">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-emerald-500/20 text-emerald-400">
                    {admin?.name?.charAt(0) || 'A'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{admin?.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p>{admin?.name}</p>
                  <p className="text-xs text-muted-foreground">{roleDisplay.label}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/owner-admin/settings">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-400">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className={cn(
        "lg:pl-64",
        viewAsRole ? "pt-24 lg:pt-10" : "pt-14 lg:pt-0"
      )}>
        {/* Top bar */}
        <div className={cn(
          "hidden lg:flex h-16 items-center justify-between px-6 border-b border-white/15 bg-card/50",
          viewAsRole ? "mt-0" : ""
        )}>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search customers, users..."
                className="w-80 pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {viewAsRole && (
              <Badge variant="outline" className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                <Eye className="h-3 w-3 mr-1" />
                Preview Mode
              </Badge>
            )}
            <LastDeployedBadge />
            <Badge variant="outline" className={roleDisplay.color}>
              {roleDisplay.label}
            </Badge>
            <span className="text-sm text-muted-foreground">{admin?.email}</span>
          </div>
        </div>

        {/* Stale build alert (renders only when backend boot > 24h) */}
        <DeployAlertBanner />

        {/* Page content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
